import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Fragment } from 'react';
import AddToCart from "./AddToCart";
import { translate } from "../Services/translation";
/**
 * @typedef {Object} Currency
 * @property {number} exchangeRate - The exchange rate of the currency.
 * @property {string|null} groupSeparator - The group separator used in the currency format.
 * @property {string} id - The ID of the currency.
 * @property {boolean} isBaseCurrency - Indicates if this is the base currency.
 * @property {string} symbol - The symbol of the currency.
 * @property {number} symbolPosition - The position of the currency symbol.
 * @property {string} systemId - The system ID of the currency.
 * @property {string} textFormat - The text format for the currency.
 */
/**
 * @typedef {Object} PriceDetails
 * @property {number} minimumQuantity - The minimum quantity for the price.
 * @property {number} price - The price of the product.
 * @property {number} priceWithVat - The price of the product including VAT.
 * @property {string} unitOfMeasurementSystemId - The system ID for the unit of measurement.
 * @property {string|null} unitOfMeasurementTitle - The title of the unit of measurement.
 * @property {number} vatPercentage - The VAT percentage applied to the price.
 */
/**
 * @typedef {Object} Price
 * @property {PriceDetails} campaignPrice - The campaign price if available.
 * @property {string} formattedCampaignPrice - The formatted campaign price.
 * @property {Currency} currency - The currency details.
 * @property {boolean} hasPrice - Indicates if the product has a price.
 * @property {PriceDetails} price - The price details.
 * @property {string} formattedPrice - The formatted price.
 * @property {Array} tierPrices - The tier prices if available.
 */
/**
 * @typedef {Object} Product
 * @property {string} articleNumber - The article number of the product.
 * @property {string} id - The ID of the product.
 * @property {boolean} isVariant - Indicates if the product is a variant.
 * @property {boolean} isExactMatch - Indicates if the product is an exact match based on article number.
 * @property {boolean} showQuantityField - Indicates if the quantity field should be shown.
 * @property {boolean} isInStock - Indicates if the product is in stock.
 * @property {boolean} isAnonymousUser - Indicates if the user is anonymous.
 * @property {Price} price - The price details of the product.
 * @property {string} quantityFieldId - The ID of the quantity field.
 * @property {boolean} hidePrice - Indicates if the price should be hidden.
 * @property {string} category - The category of the product.
 * @property {string|null} categoryLabel - The label of the category in a different language if available.
 * @property {string} name - The name of the product.
 * @property {string} url - The URL to the product page.
 * @property {string} imageSource - The source path for the product image.
 * @property {boolean} hasImage - Indicates if the product has an image.
 * @property {boolean} showAll - Flag to show the "show all results" button.
 */
/**
 * @param {Product} item
 * @returns {boolean}
 */
const isItemBuyable = (item) => {
    return !item.isAnonymousUser && item.isExactMatch && item?.price?.hasPrice;
};
const isQuotationProduct = (item) => {
    return item?.price?.price?.price == 0 || item.hidePrice;
};
const SearchResultItem = ({ item, searchUrl, index, selectedItem }) => {
    let liClass = `quick-search-result__item ${item.showAll ? 'hide' : ''} ${selectedItem === index
        ? 'quick-search-result__item--selected'
        : ''}`.trim();
    let linkClass = item.showAll
        ? 'hide'
        : `quick-search-result__link ${item.url
            ? ''
            : 'quick-search-result__link--disabled'} ${!isItemBuyable(item) ? 'visitor' : ''}`.trim();
    return (_jsxs("li", { className: liClass, children: [_jsxs("a", { className: linkClass, href: item.showAll ? searchUrl : ((item.replacementProductUrl && !item.isAnonymousUser) ? item.replacementProductUrl : item.url), children: [item.hasImage && item.imageSource && (_jsx("div", { className: 'quick-search-result__image-container', children: _jsx("img", { className: "quick-search-result__image", src: item.imageSource }) })), _jsxs("div", { className: 'quick-search-result__name-and-sku', children: [_jsx("div", { className: 'quick-search-result__name', children: item.name }), typeof item.articleNumber !== 'undefined' && item.articleNumber.length > 0 && (_jsxs("div", { className: 'quick-search-result__sku', children: [translate('nmas.quicksearchheader.resultitem.articlenr.label'), ": ", item.articleNumber] }))] })] }), isItemBuyable(item) && _jsx("div", { className: 'quick-search-result__add-to-cart', children: _jsx(AddToCart, { variant: item, isQuotationProduct: isQuotationProduct(item) }) })] }));
};
const QuickSearchResultSubList = ({ result, searchUrl, label }) => {
    if (result.length) {
        let first = result[0];
        label = first.categoryLabel;
    }
    let windowWidth = window.innerWidth;
    let maxItemsToRender = windowWidth < 992 ? 4 : 8;
    return (_jsxs(_Fragment, { children: [_jsx("li", { className: "quick-search-result__item quick-search-result__group-header", children: label }), result.length > 0 ? result.map((item, index) => (_jsx(Fragment, { children: index < maxItemsToRender ? (_jsx(SearchResultItem, { item, index, searchUrl })) : null }, `${item.name}-${index}`))) : (_jsx(Fragment, { children: _jsx("li", { className: "quick-search-result__item quick-search-result__no-hit", children: _jsx("div", { className: "quick-search-result__link", children: translate('nmas.search.nohit') }) }) }, `${label}-nohit`))] }));
};
/**
 *
 * @param {Product[]} result
 * @param selectedItem
 * @param searchUrl
 * @returns {JSX.Element}
 * @constructor
 */
const QuickSearchResult = ({ result, selectedItem, searchUrl }) => {
    // Preserve hide_internal parameter when navigating to full search results
    const currentParams = new URLSearchParams(window.location.search);
    const hideInternalActive = currentParams.get('hide_internal') === '1';
    const searchUrlWithParam = hideInternalActive ? `${searchUrl}${searchUrl.includes('?') ? '&' : '?'}hide_internal=1` : searchUrl;
    let showAllItem;
    if (result && result.length) {
        let showAllFilterResult = result.filter(x => x.showAll === true);
        if (showAllFilterResult.length) {
            showAllItem = showAllFilterResult[0];
        }
    }
    let hasExactMatch = result.some(r => typeof r.isExactMatch !== 'undefined' && r.isExactMatch === true);
    let productColClasses = hasExactMatch ? "small-12" : "small-12 large-7";
    let resultProducts = result.filter(p => p.category == "Products" || p.category === "ExactMatch");
    let resultBrands = result.filter(p => p.category == "Brands");
    let resultCategories = result.filter(p => p.category == "Categories");
    return (_jsxs("div", { className: "quick-search-result", children: [_jsx("div", { className: "quick-search-result__list-container", children: _jsxs("div", { className: "quick-search-result__list row", children: [_jsx("div", { className: `columns ${productColClasses} quick-search-result__sub-list quick-search-result__sub-list--products`, children: _jsx(QuickSearchResultSubList, { result: resultProducts, searchUrl: searchUrlWithParam, selectedItem, label: translate('nmas.quicksearchheader.products') }) }), !hasExactMatch && (_jsx("div", { className: "columns small-12 large-5 py-3 bg:ice quick-search-result__other-results", children: _jsxs("div", { className: "row", children: [_jsx("div", { className: "columns small-12 medium-6 large-12 xlarge-6 quick-search-result__sub-list", children: _jsx(QuickSearchResultSubList, { result: resultCategories, searchUrl: searchUrlWithParam, selectedItem, label: translate('nmas.quicksearchheader.categories') }) }), _jsx("div", { className: "columns small-12 medium-6 large-12 xlarge-6 quick-search-result__sub-list", children: _jsx(QuickSearchResultSubList, { result: resultBrands, searchUrl: searchUrlWithParam, selectedItem, label: translate('nmas.quicksearchheader.brands') }) })] }) }))] }) }), showAllItem && (_jsx("div", { className: 'quick-search-result__show-all-container', children: _jsx("a", { className: 'button button-primary quick-search-result__show-all', href: searchUrlWithParam, children: showAllItem.name }) }))] }));
};
export default QuickSearchResult;
